import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Save, FolderOpen, Trash2, Loader2 } from "lucide-react";
import { projectApi, formatApiError, useAuth } from "../lib/auth";
import { toast } from "sonner";

// Save / Load buttons + dialogs. Renders nothing if user is not authenticated.
// Props:
//   getState: () => object — snapshot of current wizard state to persist
//   setState: (object) => void — restore a loaded snapshot
//   currentName / setCurrentName: optional, lets the loader pre-fill the name
//   currentId / setCurrentId: tracks the active project id (so Save → Update for existing)
export default function SaveLoadProject({
  getState,
  setState,
  currentName,
  setCurrentName,
  currentId,
  setCurrentId,
}) {
  const { user } = useAuth();
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [name, setName] = useState(currentName || "");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (!saveOpen) return;
    setName(currentName || "");
  }, [saveOpen, currentName]);

  const refreshList = async () => {
    setListLoading(true);
    try {
      const items = await projectApi.list();
      setList(items);
    } catch (err) {
      toast.error("Could not load projects: " + formatApiError(err));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (loadOpen) refreshList();
  }, [loadOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  const doSave = async () => {
    if (!name.trim()) {
      toast.error("Project name is required");
      return;
    }
    setBusy(true);
    try {
      const payload = getState();
      let saved;
      if (currentId) {
        // Update in place
        saved = await projectApi.update(currentId, name.trim(), payload);
        toast.success("Project updated");
      } else {
        saved = await projectApi.create(name.trim(), payload);
        if (setCurrentId) setCurrentId(saved.id);
        toast.success("Project saved");
      }
      if (setCurrentName) setCurrentName(saved.name);
      setSaveOpen(false);
    } catch (err) {
      toast.error("Save failed: " + formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doLoad = async (id) => {
    setBusy(true);
    try {
      const proj = await projectApi.get(id);
      setState(proj.payload || {});
      if (setCurrentId) setCurrentId(proj.id);
      if (setCurrentName) setCurrentName(proj.name);
      toast.success(`Loaded "${proj.name}"`);
      setLoadOpen(false);
    } catch (err) {
      toast.error("Load failed: " + formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id, projName) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${projName}"? This can't be undone.`)) return;
    try {
      await projectApi.remove(id);
      toast.success("Project deleted");
      if (currentId === id && setCurrentId) setCurrentId(null);
      refreshList();
    } catch (err) {
      toast.error("Delete failed: " + formatApiError(err));
    }
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid="save-load-bar">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setLoadOpen(true)}
          data-testid="open-load-btn"
        >
          <FolderOpen className="h-4 w-4 mr-1" /> Load
        </Button>
        <Button
          size="sm"
          onClick={() => setSaveOpen(true)}
          className="bg-foreground hover:bg-foreground/90 text-background"
          data-testid="open-save-btn"
        >
          <Save className="h-4 w-4 mr-1" /> {currentId ? "Update" : "Save"}
        </Button>
      </div>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle>{currentId ? "Update Project" : "Save Project"}</DialogTitle>
            <DialogDescription>
              {currentId
                ? "Saves changes to the currently loaded project."
                : "Stores the current wizard state under the given name."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="proj-name">Project Name</Label>
            <Input
              id="proj-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="save-name-input"
              placeholder="e.g. Baptist Hospital East"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={doSave}
              disabled={busy || !name.trim()}
              className="bg-foreground hover:bg-foreground/90 text-background"
              data-testid="save-confirm-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {currentId ? "Update" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Load dialog */}
      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="load-dialog"
        >
          <DialogHeader>
            <DialogTitle>Your Projects</DialogTitle>
            <DialogDescription>
              Click <b>Load</b> to restore a saved project into the wizard.
            </DialogDescription>
          </DialogHeader>
          {listLoading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : list.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8" data-testid="empty-projects">
              No saved projects yet. Click <b>Save</b> to store the current selection.
            </div>
          ) : (
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => (
                  <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(p.updated_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => doLoad(p.id)}
                        data-testid={`load-btn-${p.id}`}
                      >
                        Load
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => doDelete(p.id, p.name)}
                        data-testid={`delete-btn-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
